import {Box, HBox} from '../../components/core/view';
import {H2Text, H6Text} from '../../components/core/text';
import {
  PERMISSIONS,
  check,
  checkLocationAccuracy,
} from 'react-native-permissions';
import {Platform, StyleSheet} from 'react-native';
import React, {useCallback, useState} from 'react';
import {useColorThemeType, useFontTheme} from '../../appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {EventType} from '@buncha/config/analytics/type';
import DeliveryVanIcon from '@buncha/components/core/icon/DeliveryVanIcon';
import DriverLoginModal from '@buncha/AboutShift/component/driverLoginModal';
import {DriverScreens} from '@buncha/driver/navigation/type';
import {HomeScreens} from '../navigation/types';
import If from '../../components/conditional/If';
import LocationPermissionModal from '@buncha/AboutShift/component/LocationPermissionModal';
import {MainScreens} from '../../navigation/types';
import {Modules} from '@buncha/utils/types';
import PluseIcon from '../../components/core/icon/PluseIcon';
import {Touchable} from '../../components/core/touchable';
import {navigation} from '../../config/navigation';
import {useAppSelector} from '../../config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {isWeb} from '@buncha/utils/common';

interface HomeHeaderProps {
  openWIWModal?: () => void;
  headerTitle: string;
  nameOnly?: boolean;
  showOnfleet?: boolean;
}

function HomeHeader(props: HomeHeaderProps) {
  const defaultTheme = useColorThemeType();
  const mortiseFont = useFontTheme('mortise');
  const muliFont = useFontTheme('muli');
  const {title} = useGetLocale(Modules.home, MainScreens.Home) ?? {};
  const [driverLoginModal, setDriverLoginModal] = useState(false);
  const [locationPermissionModal, setLocationPermissionModal] = useState(false);

  const isDriverLoggedIn = Boolean(
    useAppSelector(gState => gState.home.driver?.id),
  );

  const toggleDriverModal = useCallback(() => {
    setDriverLoginModal(s => !s);
  }, []);

  const toggleLocationPermissionModal = useCallback(() => {
    setLocationPermissionModal(s => !s);
  }, []);

  const onBoardingState = useAppSelector(gstate => gstate.onboarding);
  const whenIWorkData = onBoardingState.currentUser?.WhenIWorkInfo;

  const navigateToOpenShifts = useCallback(() => {
    if (whenIWorkData?.login?.isLogin)
      navigation.navigate(MainScreens.Home, {
        screen: HomeScreens.OpenShifts,
      });
    else if (props.openWIWModal) props.openWIWModal();
  }, [props, whenIWorkData?.login?.isLogin]);

  const navigateToDriverOrders = useCallback(async () => {
    const access = Platform.select({
      android: PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION,
      ios: PERMISSIONS.IOS.LOCATION_ALWAYS,
    });
    if (!access) return;
    const status = await check(access);
    const iOSPreciseStatus =
      status === 'granted' && Platform.OS === 'ios'
        ? await checkLocationAccuracy()
        : 'reduced';
    const preciseStatus = Platform.OS === 'ios' ? iOSPreciseStatus : 'full';
    if (status !== 'granted' || preciseStatus !== 'full')
      return toggleLocationPermissionModal();
    if (!isDriverLoggedIn) return toggleDriverModal();
    Analytics.eventWithProps('Access onfleet route', {
      type: EventType.cta,
      userType: 'driver',
    });
    navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.DriverOrders,
      params: {
        backPage: HomeScreens.YourSchedule,
      },
    });
  }, [isDriverLoggedIn, toggleDriverModal, toggleLocationPermissionModal]);

  return (
    <Box
      style={[
        styles.mainContainer,
        {
          backgroundColor: defaultTheme?.background.blue[300],
        },
      ]}>
      <HBox
        style={[
          styles.mainSubContainer,
          {
            backgroundColor: defaultTheme?.background.blue[300],
          },
        ]}>
        <H2Text
          style={[
            {
              color: defaultTheme?.text.white[100],
              fontFamily: mortiseFont[700].normal,
            },
          ]}>
          {props.headerTitle}
        </H2Text>
        <If condition={!props.nameOnly}>
          <Touchable
            onPress={navigateToOpenShifts}
            style={[
              styles.shiftBox,
              {
                backgroundColor: defaultTheme?.background.blue[300],
              },
            ]}>
            <H6Text
              style={[
                styles.subText,
                {
                  color: defaultTheme?.text.white[100],
                  fontFamily: muliFont[700].normal,
                },
              ]}>
              {title?.addShifts}
            </H6Text>
            <PluseIcon
              fill={defaultTheme?.background.white[100]}
              style={styles.iconStyle}
            />
          </Touchable>
        </If>
        <If condition={props.showOnfleet && !isWeb()}>
          <Touchable
            onPress={navigateToDriverOrders}
            style={[
              styles.shiftBox,
              {
                backgroundColor: defaultTheme?.background.blue[300],
                borderColor: defaultTheme?.text.white[100],
              },
            ]}>
            <DeliveryVanIcon
              fill={defaultTheme?.text.white[100]}
              height={24}
              width={24}
              viewBox="0 -5 35 35"
            />
            <H6Text
              style={[
                styles.subText,
                {
                  color: defaultTheme?.text.white[100],
                  fontFamily: muliFont[700].normal,
                },
              ]}>
              {title?.accessOnfleet}
            </H6Text>
          </Touchable>
        </If>
      </HBox>
      <DriverLoginModal
        open={driverLoginModal}
        onClose={toggleDriverModal}
        source={'Home Page'}
      />
      <If condition={locationPermissionModal}>
        <LocationPermissionModal
          open={locationPermissionModal}
          onClose={toggleLocationPermissionModal}
        />
      </If>
    </Box>
  );
}

export default React.memo(HomeHeader);

const styles = StyleSheet.create({
  mainContainer: {
    justifyContent: 'flex-end',
  },
  mainSubContainer: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  iconStyle: {
    alignSelf: 'flex-end',
  },
  subText: {
    marginRight: 5,
  },
  shiftBox: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    padding: 5,
    paddingLeft: 10,
    borderRadius: 100,
    gap: 5,
  },
});
